// ─── Feuille : FILTRES (catégories / types / garde / vacances) ────
// Extrait d'EcranCalendrier (découpage). BROUILLON local : la sélection ne
// s'applique qu'à la 💾 (principe app-wide) — le composant porte ses états
// et renvoie le résultat via onAppliquer({ masques, garde, vac }).
import React, { useEffect, useMemo, useState } from 'react';
import { Text, TouchableOpacity, View, Switch, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, useUI, useT, HIT, confirmerChoix } from '@mop/core';
import { CATEGORIES_EVT, TYPE_FILTRES, labelCategorie, labelTypeFiltre } from './data/categories';

export default function FeuilleFiltres({
  visible, ongletInitial = 'cat', estCoparent, avecVacances = true, masques, gardeVisible, vacVisible, onAppliquer, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [onglet, setOnglet] = useState('cat');        // 'cat' | 'type'
  const [brouillon, setBrouillon] = useState(null);   // Set en cours d'édition
  const [gardeBru, setGardeBru] = useState(true);
  const [vacBru, setVacBru] = useState(true);

  // Ouverture : brouillons initialisés depuis l'état APPLIQUÉ.
  useEffect(() => {
    if (!visible) return;
    setOnglet(ongletInitial === 'type' ? 'type' : 'cat');
    setBrouillon(new Set(masques));
    setGardeBru(gardeVisible);
    setVacBru(vacVisible);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const bru = brouillon || masques;
  const filtreModifie = (!!brouillon
    && (brouillon.size !== masques.size || [...brouillon].some(x => !masques.has(x))))
    || (estCoparent && gardeBru !== gardeVisible) || vacBru !== vacVisible;
  const toggleBrouillon = (id) => setBrouillon(prev => {
    const n = new Set(prev || masques); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const idsOnglet = onglet === 'cat' ? CATEGORIES_EVT.map(c => c.id) : [...TYPE_FILTRES.map(ty => ty.id), 'type:autre'];
  const reinitialiser = () => confirmerChoix(`${t('calendrier.reinitFiltres')} ↺`, t('calendrier.reinitMsg'), [
    { text: t('calendrier.reinitOnglet', { onglet: onglet === 'cat' ? t('calendrier.categories') : t('calendrier.types') }),
      onPress: () => setBrouillon(prev => { const n = new Set(prev || masques); idsOnglet.forEach(id => n.delete(id)); return n; }) },
    { text: t('calendrier.reinitTout'), onPress: () => setBrouillon(new Set()) },
    { text: t('commun.annuler'), style: 'cancel' },
  ]);
  const nbCat = CATEGORIES_EVT.filter(c => bru.has(c.id)).length;
  const nbTyp = TYPE_FILTRES.filter(ty => bru.has(ty.id)).length;
  const lignes = onglet === 'cat'
    ? CATEGORIES_EVT.map(c => ({ id: c.id, label: labelCategorie(c.id, t) }))
    : TYPE_FILTRES.map(ty => ({ id: ty.id, label: labelTypeFiltre(ty.id, t) }));

  return (
    <Feuille visible={visible} titre={t('calendrier.filtres')} emoji="🏷️" hauteur={0.8}
      onFermer={onFermer}
      action={{ icone: '💾', label: t('commun.enregistrer'), disabled: !filtreModifie,
        onPress: () => onAppliquer({ masques: new Set(brouillon || masques), garde: gardeBru, vac: vacBru }) }}
      boutons={(
        <TouchableOpacity hitSlop={HIT} onPress={reinitialiser} accessibilityLabel={t('calendrier.reinitFiltres')}>
          <Text style={{ fontSize: 22, color: UI.accent, fontWeight: '700' }}>↺</Text>
        </TouchableOpacity>
      )}>
      <Text style={s.filtreIntro}>{t('calendrier.filtresIntro')}</Text>
      {/* Garde : afficher / cacher pastilles, anneaux et légende (co-parental). */}
      {estCoparent && (
        <View style={s.switchRow}>
          <Text style={s.switchLbl}>👪 {t('calendrier.afficherGarde')}</Text>
          <Switch value={gardeBru} onValueChange={setGardeBru}
            trackColor={{ false: UI.champBord, true: UI.accent }} thumbColor="#fff" />
        </View>
      )}
      {/* Vacances : bascule masquee si la fonction n'est pas incluse. */}
      {avecVacances && (
        <View style={s.switchRow}>
          <Text style={s.switchLbl}>🏖️ {t('calendrier.afficherVacances')}</Text>
          <Switch value={vacBru} onValueChange={setVacBru}
            trackColor={{ false: UI.champBord, true: UI.accent }} thumbColor="#fff" />
        </View>
      )}
      {/* Onglets segmentés (ChoixPuces pleine) — comme les vues M/S/J. */}
      <ChoixPuces pleine value={onglet} onChange={setOnglet}
        options={[
          { id: 'cat', label: `🏷️ ${t('calendrier.categories')}${nbCat ? ` · ${nbCat}` : ''}` },
          { id: 'type', label: `📌 ${t('calendrier.types')}${nbTyp ? ` · ${nbTyp}` : ''}` },
        ]} />
      <View style={{ height: 10 }} />
      {lignes.map(l => {
        const vis = !bru.has(l.id);
        return (
          <TouchableOpacity key={l.id} style={s.filtreLigne} onPress={() => toggleBrouillon(l.id)}>
            <Text style={s.filtreLbl}>{l.label}</Text>
            <Text style={[s.filtreCase, vis && s.filtreCaseOn]}>{vis ? '☑' : '☐'}</Text>
          </TouchableOpacity>
        );
      })}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  filtreIntro: { fontSize: 12, color: UI.muted, marginBottom: 6 },
  filtreLigne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, borderTopWidth: 0.5, borderTopColor: UI.border },
  filtreLbl: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '600' },
  filtreCase: { fontSize: 20, color: UI.faint },
  filtreCaseOn: { color: UI.accent },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingVertical: 4 },
  switchLbl: { fontSize: 14, fontWeight: '700', color: UI.ink },
});
